import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// 🔧 Normalize API base so we don't end up with /api/api/...
const RAW = process.env.REACT_APP_API_BASE || "";
const API_BASE = RAW.replace(/\/+$/, ""); // strip trailing slashes
const APIv1 = API_BASE.endsWith("/api") ? API_BASE : `${API_BASE}/api`;

export default function ManageStaff() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("staffToken"), []);
  const auth = useMemo(
    () => (token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : {}),
    [token]
  );

  // redirect if not logged in
  useEffect(() => {
    if (!token) navigate("/staff/login", { replace: true });
  }, [token, navigate]);

  // state
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // create form state
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);

  // temp password modal
  const [tempInfo, setTempInfo] = useState(null); // { email, password }

  const load = async (goToFirstPage = false) => {
    if (goToFirstPage) setPage(1);
    if (!token) return;
    try {
      setLoading(true);
      setErr("");
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (q.trim()) qs.set("q", q.trim());
      const res = await fetch(`${APIv1}/staff/users?${qs.toString()}`, { headers: auth });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr("Failed to load staff users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setCreating(true);
      const res = await fetch(`${APIv1}/staff/users`, {
        method: "POST",
        headers: auth,
        body: JSON.stringify({ email: email.trim(), role, password: password || undefined }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTempInfo({ email: data.email, password: data.tempPassword });
      setEmail("");
      setRole("staff");
      setPassword("");
      load(true);
    } catch {
      setErr("Failed to create staff user.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    await fetch(`${APIv1}/staff/users/${id}/revoke`, { method: "POST", headers: auth });
    load();
  };

  const reactivate = async (id) => {
    await fetch(`${APIv1}/staff/users/${id}/reactivate`, { method: "POST", headers: auth });
    load();
  };

  const resetPw = async (id) => {
    const res = await fetch(`${APIv1}/staff/users/${id}/reset-password`, {
      method: "POST",
      headers: auth,
      body: JSON.stringify({}),
    });
    if (!res.ok) return;
    const data = await res.json();
    const u = rows.find((r) => r.id === id);
    setTempInfo({ email: u?.email || "user", password: data.tempPassword });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const logout = () => {
    localStorage.removeItem("staffToken");
    navigate("/staff/login", { replace: true });
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-semibold">Manage Staff</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/staff")}
            className="h-10 px-4 text-sm rounded-lg border border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100"
          >
           Staff Home
          </button>
          <button
            onClick={() => load()}
            className="h-10 px-4 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900"
          >
            Refresh
          </button>
          <button
            onClick={logout}
            className="h-10 px-4 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Error */}
      {err && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Create form */}
      <form onSubmit={onCreate} className="bg-white border rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-sm mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 px-3 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-10 px-3 border rounded-lg"
            >
               <option value="sales">sales</option>
                <option value="marketing">marketing</option>
                <option value="tech">tech</option>
                <option value="admin">admin</option>
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-sm mb-1">Set Password (optional)</label>
            <input
              type="text"
              placeholder="Leave blank to auto-generate"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 px-3 border rounded-lg"
            />
          </div>
          <button
            type="submit"
            disabled={creating}
            className="h-10 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {creating ? "Creating…" : "Create Staff"}
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Role</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Last Login</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-500">
                  No staff found
                </td>
              </tr>
            ) : (
              rows.map((u) => (
                <tr key={u.id} className="border-t">
                  <td className="p-3">{u.email}</td>
                  <td className="p-3">{u.role}</td>
                  <td className="p-3">{u.isActive ? "Active" : "Revoked"}</td>
                  <td className="p-3">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                        {u.isActive ? (
                        <button
                            onClick={() => revoke(u.id)}
                            className="h-9 px-3 rounded-lg bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
                        >
                            Revoke
                        </button>
                        ) : (
                        <button
                            onClick={() => reactivate(u.id)}
                            className="h-9 px-3 rounded-lg bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                        >
                            Reactivate
                        </button>
                        )}
                        <button
                        onClick={() => resetPw(u.id)}
                        className="h-9 px-3 rounded-lg bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400"
                        >
                        Reset PW
                        </button>
                    </div>
                    </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Page {page} of {totalPages} • {total} total
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Temp password modal */}
      {tempInfo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-2">Temporary Password</h2>
            <p className="text-sm text-gray-700 mb-4">
              Share this password with <b>{tempInfo.email}</b> (one time). They should change it after first login.
            </p>
            <div className="p-3 border rounded-lg font-mono break-all">{tempInfo.password}</div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setTempInfo(null)}
                className="h-9 px-3 border rounded-lg bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
